import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Input, Typography, useInput } from '@chilecompra/react-kit/components'
import {
  ScrollableCard,
  Strong,
  Grid,
  Section,
  SectionInputContainer,
  SectionInputNameContainer
} from './AgreementInformation.styles'
import { getDateMessage, formatDateMessage } from '../../../modules/utils/dates'
import { onSetDocumentValueThunk, onSetSignerNameThunk } from '../RegisterWizard.actions'

/**
 * The Agreement informacion section container.
 */
const AgreementInformation = () => {
  const dispatch = useDispatch()
  const { business, director, surrogantsList, documentValue } = useSelector(state => state.registerWizard)
  const { signer, isCivilRegistry } = useSelector(state => state.registerWizard.signerIdentification)
  const initialDocumentValue = useSelector(state => state.registerWizard.documentValue)

  const getCurrentDirector = () => {
    if (director && Object.keys(director).length > 0) {
      return { ...director, isSubrogant: false }
    } else if (surrogantsList && surrogantsList.length > 0) {
      const [firstSurrogant] = [...surrogantsList].sort((a, b) => a.order - b.order)

      return { ...firstSurrogant, isSubrogant: true }
    }

    return {}
  }

  const [currentDirector, setCurrentDirector] = useState(getCurrentDirector())

  const changeCallbackName = updatedValue => {
    dispatch(
      onSetSignerNameThunk({
        name: updatedValue,
        lastName: signer.lastName
      })
    )
  }

  const changeCallbackLastName = updatedValue => {
    dispatch(
      onSetSignerNameThunk({
        name: signer.name,
        lastName: updatedValue
      })
    )
  }

  const changeCallbackDocumentValue = updatedValue => {
    dispatch(onSetDocumentValueThunk(updatedValue))
  }

  const {
    error: documentError,
    value: documentValueInput,
    onChange: handleDocumentChange
  } = useInput({
    initialValue: initialDocumentValue,
    changeCallback: changeCallbackDocumentValue,
    overwriteCallback: () => documentValue || ''
  })

  const {
    error: signerNameError,
    value: signerNameValue,
    onChange: handleSignerNameChange
  } = useInput({
    initialValue: signer?.name || '',
    changeCallback: changeCallbackName,
    overwriteCallback: () => signer.name || ''
  })

  const {
    error: signerLastNameError,
    value: signerLastNameValue,
    onChange: handleSignerLastNameChange
  } = useInput({
    initialValue: signer?.lastName || '',
    changeCallback: changeCallbackLastName,
    overwriteCallback: () => signer.lastName
  })

  useEffect(() => {
    setCurrentDirector(getCurrentDirector())
  }, [director, surrogantsList])

  const renderDirectorInfo = () => {
    if (!currentDirector || !currentDirector.firstName) return null

    if (currentDirector.isSubrogant) {
      return (
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          La personería de {getDirectorTitle()}(s) de la <Strong>DIRECCIÓN DE COMPRAS Y CONTRATACIÓN PÚBLICA</Strong>,{' '}
          {currentDirector.gender === 'F' ? 'doña' : 'don'}{' '}
          <Strong>
            {currentDirector.firstName ? currentDirector.firstName : 'Nombre'}{' '}
            {currentDirector.lastName ? currentDirector.lastName : 'Apellidos'}
          </Strong>
          , consta en Resolución RA N°{' '}
          {currentDirector.resolutionDecreeAppointment ? currentDirector.resolutionDecreeAppointment : 'N°XXXXX'}, de{' '}
          {currentDirector.dateFrom ? formatDateMessage(currentDirector.dateFrom) : 'dateFrom'}, del de la Dirección de
          Compras y Contratación Pública, en relación con el artículo 80 del DFL N° 29, de 2004, del Ministerio de
          Hacienda que fija el texto refundido, coordinado y sistematizado de la Ley N° 18.834, sobre Estatuto
          Administrativo.
        </Typography>
      )
    } else {
      return (
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          La personería de {currentDirector.gender === 'F' ? 'doña' : 'don'}{' '}
          <Strong>
            {currentDirector.firstName ? currentDirector.firstName : 'Nombre'}{' '}
            {currentDirector.lastName ? currentDirector.lastName : 'Apellidos'}
          </Strong>
          , para actuar en representación de la <Strong>DIRECCIÓN DE COMPRAS Y CONTRATACIÓN PÚBLICA</Strong> consta en
          el Decreto{' '}
          {currentDirector.resolutionDecreeAppointment ? currentDirector.resolutionDecreeAppointment : 'N°XXXXX'}, de{' '}
          {currentDirector.dateFrom ? formatDateMessage(currentDirector.dateFrom) : 'dateFrom'}, del Ministerio de
          Hacienda.
        </Typography>
      )
    }
  }

  const getDirectorTitle = () => {
    if (!currentDirector || !currentDirector.gender) return 'Director'
    if (currentDirector.gender === 'F') return 'Directora'

    return 'Director'
  }

  const getDirectorPrefix = () => {
    if (!currentDirector || !currentDirector.gender) return 'don'

    return currentDirector.gender === 'F' ? 'doña' : 'don'
  }

  const isSubrogant = currentDirector && currentDirector.isSubrogant

  return (
    <ScrollableCard background="blank">
      <Section background="blank" item lg={12} xs={12}>
        <Typography tabIndex="0" fontWeight="bold" margin="0 0 20px 0" variant="h4" align="center">
          <Typography variant="body1" margin="0 0 7px" fontWeight="bold">
            CONVENIO DE ADHESIÓN AL SISTEMA DE COMPRAS PÚBLICAS
          </Typography>
          <Typography variant="body1" margin="0 0 7px" fontWeight="bold">
            ENTRE
          </Typography>
          <Typography variant="body1" margin="0 0 7px" fontWeight="bold">
            DIRECCIÓN DE COMPRAS Y CONTRATACIÓN PÚBLICA
          </Typography>
          <Typography variant="body1" margin="0 0 7px" fontWeight="bold">
            Y
          </Typography>
          <Typography variant="body1" margin="0 0 10px" fontWeight="bold">
            {business?.name ? business?.name : 'XXXXXXXXXXXXXXXX'}
          </Typography>
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          En Santiago, a {getDateMessage()}, entre la <Strong>DIRECCIÓN DE COMPRAS Y CONTRATACIÓN PÚBLICA</Strong>,
          R.U.T. N°60.808.000-7, en adelante <Strong>“la DIRECCIÓN CHILECOMPRA”</Strong> o{' '}
          <Strong>“la DIRECCIÓN”</Strong>, representada por su {getDirectorTitle()}
          {isSubrogant && '(s)'}, {getDirectorPrefix()}{' '}
          <Strong>
            {currentDirector?.firstName ? currentDirector.firstName.toUpperCase() : 'Nombre'}{' '}
            {currentDirector?.lastName ? currentDirector.lastName.toUpperCase() : 'Apellidos'}
          </Strong>
          , cédula nacional de identidad N° {currentDirector?.dni ? currentDirector.dni : 'rut'}, domiciliados todos,
          para estos efectos, en Monjitas N°392, piso 8, de la comuna y ciudad de Santiago, por una parte; y por la
          otra, <Strong>{business?.name ? business?.name : 'XXXXXXXXXXXXXXXX'}</Strong>, R.U.T. N°
          {business?.dni ? business?.dni : 'XXXXXXX'}, en adelante <Strong>“EL ORGANISMO”</Strong>, representada por{' '}
          {signer?.gender === 'F' ? 'doña' : 'don'} {signer?.fullName ? signer?.fullName?.toUpperCase() : 'Nombre'},
          cédula de identidad N°
          {signer?.dni ? signer?.dni : 'dni'}, ambos domiciliados en{' '}
          {business?.address ? business?.address : 'XXXXXXXXXXXXXXXX'}; se ha convenido lo siguiente:
        </Typography>
        <Typography tabIndex="0" fontWeight="bold" variant="h4">
          PRIMERO: PERSONERÍAS
        </Typography>
        {renderDirectorInfo()}
        <Section>
          <Typography tabIndex="0" variant="body1" margin="0 0 20px">
            La personería de {!isCivilRegistry ? 'don/doña' : signer?.gender === 'F' ? 'doña' : 'don'}{' '}
            {!isCivilRegistry ? (
              <>
                <SectionInputNameContainer>
                  <Input
                    aria-label="Nombre"
                    error={signerNameError}
                    value={signerNameValue}
                    onChange={handleSignerNameChange}
                  />
                </SectionInputNameContainer>
                <SectionInputNameContainer>
                  <Input
                    aria-label="Apellido"
                    error={signerLastNameError}
                    value={signerLastNameValue}
                    onChange={handleSignerLastNameChange}
                  />
                </SectionInputNameContainer>
              </>
            ) : (
              <Strong>{signer?.fullName ? signer?.fullName?.toUpperCase() : 'Nombre'}</Strong>
            )}
            , para actuar en representación de {business?.name ? business?.name : 'XXXXXXXXXXX'}, consta en la
            <SectionInputContainer>
              <Input
                aria-label="consta de descripción"
                error={documentError}
                value={documentValueInput}
                onChange={handleDocumentChange}
              />
            </SectionInputContainer>
            , conforme al documento que se adjunta. La responsabilidad exclusiva del <Strong>ORGANISMO</Strong> adjuntar
            correctamente dicho documento.
          </Typography>
        </Section>

        <Typography tabIndex="0" fontWeight="bold" variant="h4">
          SEGUNDO: OBJETO
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          En el marco de las funciones y finalidades de ambas instituciones la <Strong>DIRECCION</Strong> y{' '}
          <Strong>EL ORGANISMO</Strong> han decidido celebrar un convenio de adhesión de este organismo, de forma
          voluntaria, al Sistema de Compras y Contratación Pública en los términos que se señalan en la siguiente
          cláusula tercera.
        </Typography>
        <Typography tabIndex="0" fontWeight="bold" variant="h4">
          TERCERO: ÁMBITOS DE COLABORACIÓN
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          El presente Convenio ha sido suscrito con la finalidad de concretar la{' '}
          <Strong>Adhesión voluntaria a las disposiciones de la ley Nº19.886:</Strong>
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          En virtud de lo dispuesto en el artículo 1° de la ley 19.886, podrán adherir voluntariamente las
          corporaciones, fundaciones y asociaciones en las que participe de su administración o dirección un organismo
          de la Administración del Estado, y que reciban transferencias de fondos públicos que en su conjunto sean
          inferiores a 1.500 unidades tributarias mensuales.
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          Asimismo, los organismos del Estado no incluidos en los incisos 2, 3, 4 y 5 del artículo 1° de la ley 19.886,
          el Banco Central, las empresas públicas creadas por ley y las sociedades en las que el Estado tenga
          participación accionaria de más del 50 por ciento, podrán adherir voluntariamente a las disposiciones de dicha
          ley y su reglamento, en todo aquello que no fuere contrario a lo dispuesto en sus propias leyes orgánicas.
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          En razón de lo anterior, <Strong>EL ORGANISMO</Strong> mediante el respectivo comunicado manifestó su
          intención de adherirse voluntariamente al Sistema de Compras y Contratación Pública, a los Convenios Marco
          suscritos por la <Strong>DIRECCIÓN DE COMPRAS Y CONTRATACIÓN PÚBLICA</Strong> y en definitiva a las
          disposiciones de la ley Nº19.886, solicitud que fue aceptada por esta Dirección, lo cual se formaliza mediante
          la suscripción del presente convenio. Lo anterior es procedente dado que la referida Entidad cumple con uno o
          más de los requisitos establecidos el artículo 1° de la ley 19.886. Por tanto, de acuerdo con lo dispuesto en
          la cláusula “Vigencia”, se incorporará al <Strong>ORGANISMO</Strong> al Sistema de Información de Compras y
          Contratación Pública y a los Convenios Marco suscritos por esta Dirección.
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          Además, la <Strong>DIRECCIÓN CHILECOMPRA</Strong> y <Strong>EL ORGANISMO</Strong> se prestarán colaboración
          mutua en todas las actividades necesarias para la incorporación voluntaria de dicho organismo al Sistema de
          Compras y Contratación Pública, preferentemente, en relación al uso del Sistema de Información de Compras
          Públicas y a la utilización del Catálogo Electrónico de Convenios Marco licitados por esta Dirección.
        </Typography>
        <Typography tabIndex="0" fontWeight="bold" variant="h4">
          CUARTO: OBLIGACIONES
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          En razón de la incorporación ya descrita, además de lo indicado en la cláusula precedente,{' '}
          <Strong>EL ORGANISMO</Strong> adquiere derechos y contrae obligaciones propias de la utilización del Sistema
          de Información de Compras Públicas y del uso del Catálogo de Convenios Marco. En este contexto, se exige al{' '}
          <Strong>ORGANISMO</Strong> publicar con el o los sistemas de información que establezca la Dirección, la
          “información básica relativa a sus contrataciones y aquella que establezca el reglamento”, en conformidad con
          el artículo 20 de la ley N°19.886.
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          En este contexto, <Strong>EL ORGANISMO</Strong> se obliga a lo siguiente:
        </Typography>
        <Grid container spacing={1}>
          <Grid container direction="column" spacing={2}>
            <Grid container item alignItems="top">
              <Grid item xs={1}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  a)
                </Typography>
              </Grid>
              <Grid item xs={11}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  Disponer que las autoridades del <Strong>ORGANISMO</Strong> y los funcionarios, que de manera directa
                  o indirecta efectúen o participen en procesos de compra (requirentes, área de compras, comisiones
                  evaluadoras, área legal, entre otros), deban someterse a procesos de acreditación con el objeto de
                  asegurar un correcto funcionamiento y desarrollo de las compras que se realicen mediante este Sistema,
                  y a las condiciones de dicho proceso (validación y bloqueos de claves, respectivamente).
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid container direction="column" spacing={2}>
            <Grid container item alignItems="top">
              <Grid item xs={1}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  b)
                </Typography>
              </Grid>
              <Grid item xs={11}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  Asegurar que los procesos de compra que efectúe EL ORGANISMO a través del Sistema de Información de
                  Compras y Contratación Pública, www.mercadopublico.cl, se ajusten a los principios, requisitos y
                  formalidades reguladas por la Ley N° 19.886 y su Reglamento en su totalidad. El detalle de cada uno de
                  los procesos de compra que <Strong>EL ORGANISMO</Strong> efectúe a través del Sistema deberá ser
                  indicado expresamente en el respectivo Plan de Trabajo.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid container direction="column" spacing={2}>
            <Grid container item alignItems="top">
              <Grid item xs={1}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  c)
                </Typography>
              </Grid>
              <Grid item xs={11}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  Velar por el cumplimiento de los principios de Transparencia y Probidad de la actuación pública en lo
                  referido a las adquisiciones efectuadas a través del Sistema de Información de Compras y Contratación
                  Públicas. Especialmente relevante será el cumplimiento de las obligaciones dispuestas en el Capítulo
                  VII “De la probidad administrativa y transparencia en la contratación pública” de la Ley N°19.886.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid container direction="column" spacing={2}>
            <Grid container item alignItems="top">
              <Grid item xs={1}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  d)
                </Typography>
              </Grid>
              <Grid item xs={11}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  Disponer que los funcionarios que, en forma directa o indirecta efectúen o participen en procesos de
                  compra (requirentes, área de compras, comisiones evaluadoras, área legal, entre otros), participen de
                  los programas de capacitación dispuestos en la web por la Dirección ChileCompra.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid container direction="column" spacing={2}>
            <Grid container item alignItems="top">
              <Grid item xs={1}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  e)
                </Typography>
              </Grid>
              <Grid item xs={11}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  Garantizar que las autoridades y funcionarios que correspondan den un uso responsable de la clave de
                  acceso personal, evitando el mal uso y préstamo de ésta.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid container direction="column" spacing={2}>
            <Grid container item alignItems="top">
              <Grid item xs={1}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  f)
                </Typography>
              </Grid>
              <Grid item xs={11}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  Contar con un Administrador ChileCompra, responsable de la creación de usuarios y de unidades de
                  compra, quien es el contacto entre ChileCompra y <Strong>EL ORGANISMO</Strong>.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid container direction="column" spacing={2}>
            <Grid container item alignItems="top">
              <Grid item xs={1}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  g)
                </Typography>
              </Grid>
              <Grid item xs={11}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  Someter sus procesos de contratación realizados mediante el Sistema de Información de Compras y
                  Contratación Públicas a la eventual revisión del Observatorio ChileCompra, que interviene en forma
                  focalizada a través de una gestión activa cuando detecta omisiones, errores, o prácticas que podrían
                  derivar en irregularidades en las compras públicas.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid container direction="column" spacing={2}>
            <Grid container item alignItems="top">
              <Grid item xs={1}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  h)
                </Typography>
              </Grid>
              <Grid item xs={11}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  Dar respuesta a los reclamos en los plazos estipulados en los términos y condiciones de uso del
                  Sistema de Información de Compras y Contratación Públicas.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid container direction="column" spacing={2}>
            <Grid container item alignItems="top">
              <Grid item xs={1}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  i)
                </Typography>
              </Grid>
              <Grid item xs={11}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  En todos sus procesos de compra regidos por la ley 19.886, objeto del presente convenio,{' '}
                  <Strong>EL ORGANISMO</Strong> deberá efectuar los pagos a sus proveedores dentro de los treinta días
                  corridos siguientes a la recepción de la factura o del respectivo instrumento tributario de cobro.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid container direction="column" spacing={2}>
            <Grid container item alignItems="top">
              <Grid item xs={1}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  j)
                </Typography>
              </Grid>
              <Grid item xs={11}>
                <Typography tabIndex="0" variant="body1" margin="0 0 20px">
                  <Strong>EL ORGANISMO</Strong> deberá entregar toda la información que sea necesaria para revisar el
                  total cumplimiento de las obligaciones acordadas en el presente convenio a la Dirección ChileCompra.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          Cabe destacar que el incumplimiento de algunas de las obligaciones convenidas en el presente instrumento
          facultará a la Dirección ChileCompra a solicitar su término anticipado, de acuerdo a lo dispuesto en la
          cláusula sexta de este convenio.
        </Typography>
        <Typography tabIndex="0" fontWeight="bold" variant="h4">
          QUINTO: COORDINADORES DEL CONVENIO
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          Ambas entidades nombrarán, respectivamente, un coordinador general para la implementación de este convenio y
          para el desarrollo futuro de acciones de colaboración recíprocas entre ambas instituciones.
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          Los coordinadores actuarán en representación de su respectiva institución y cumplirán el rol de facilitadores
          e intermediarios en lo que se requiera.
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          Ambas entidades elaborarán un plan de trabajo conjunto para el desarrollo del presente convenio.
        </Typography>
        <Typography tabIndex="0" fontWeight="bold" variant="h4">
          SEXTO: VIGENCIA
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          El presente convenio comenzará a regir a contar de la fecha de la total tramitación de la resolución de la
          Dirección ChileCompra que lo apruebe, salvo que por razones de buen servicio deba comenzar desde la
          suscripción del presente convenio, y tendrá vigencia indefinida, terminando anticipadamente cuando alguna de
          las partes manifieste a la otra su voluntad de ponerle término por escrito, con a lo menos sesenta días de
          anticipación a la fecha en el que el término de vigencia producirá efecto.
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          La terminación del presente convenio por una de las partes, en caso alguno dará derecho a indemnización a
          favor de la contraparte.
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          Si por cualquier modificación a la ley 19.886 o a su reglamento, <Strong>EL ORGANISMO</Strong> debe sujetarse
          obligatoriamente a todas las disposiciones que rigen el Sistema de Compras y Contratación Pública o a más
          disposiciones de las que lo obliga este instrumento, el presente convenio quedará sin efecto, debiendo dicha
          entidad regirse por las disposiciones que les resulten aplicables, conforme a la ley 19.886 y su reglamento.
        </Typography>
        <Typography tabIndex="0" fontWeight="bold" variant="h4">
          SÉPTIMO: PRESUPUESTO
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          Cada parte asumirá los costos de la implementación de lo acordado en el presente convenio respecto de las
          obligaciones que cada una asume.
        </Typography>
        <Typography tabIndex="0" fontWeight="bold" variant="h4">
          OCTAVO: DOMICILIO
        </Typography>
        <Typography tabIndex="0" variant="body1" margin="0 0 20px">
          Para todos los efectos del presente convenio, las partes fijan domicilio en la comuna y ciudad de Santiago.
        </Typography>
      </Section>
    </ScrollableCard>
  )
}

export default AgreementInformation
